import Link from "next/link";
import useOneLinkUrl from "hooks/useOneLinkUrl";
import Image from "next/image";
import LumiLogo from "@public/lumi-logo.png";
import PublisherListPhoto from "@public/publisher-list-photo.png";
import NoPopUp from "@public/no_pop_up_no_distractions.webp";
import LastApp from "@public/your_last_app_we_promise.webp";
import NumberOne from "@public/were_number_1.webp";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { useWindowWidth } from "@react-hook/window-size";
import { Trans } from "@lingui/macro";
import HomeRating from "@public/rating/home-rating.png";

type DialogStatus = "not_shown" | "showing" | "dismissed";

const SwitchToAppDialog: React.FunctionComponent<{
  cookieKey?: string;
}> = ({ cookieKey }) => {
  const switchToApp = useOneLinkUrl(
    "https://link.luminews.my/ewC9/switchtoappbanner",
    true
  );
  const width = useWindowWidth();

  const [dialogStatus, setDialogStatus] = useState<DialogStatus>("not_shown");

  const [pageViews, setPageViews] = useState(1);

  // deprecating, keeping to avoid breaking things
  const [xisOpen, setIsOpen] = useState(false);
  const [xhasOpen, setHasOpen] = useState(false);

  useEffect(() => {
    if (dialogStatus == "not_shown") {
      const timeoutDelay = 5000;
      //     const timeoutDelay = hasOpen ? 15 * 60 * 1000 : 5000;
      const timerId = setTimeout(() => {
        setDialogStatus("showing");
        let numberOfViews =
          parseInt(sessionStorage.getItem("numberOfViews"), 10) || 0;
        sessionStorage.setItem(
          "numberOfViews",
          JSON.stringify(numberOfViews + 1)
        );
        setPageViews(numberOfViews + 1);
      }, timeoutDelay);

      return () => {
        // If the component unmounts or isOpen becomes true before the timeout triggers,
        // clear the timeout to prevent it from running.
        clearTimeout(timerId);
      };
    }
  }, [dialogStatus]);

  const hasOpen = dialogStatus == "dismissed";
  const isOpen = dialogStatus == "showing";

  // const hasOpen = xhasOpen;
  // const isOpen = xisOpen;
  // useEffect(() => {
  //   if (!isOpen && !hasOpen) {
  //     const timeoutDelay = hasOpen ? 15 * 60 * 1000 : 5000;
  //     const timerId = setTimeout(() => {
  //       setIsOpen(true);
  //     }, timeoutDelay);

  //     return () => {
  //       // If the component unmounts or isOpen becomes true before the timeout triggers,
  //       // clear the timeout to prevent it from running.
  //       clearTimeout(timerId);
  //     };
  //   } else {
  //     // added a try catch block to check whether the user disables the storage and cookies or not
  //     console.log("reached here");
  //     try {
  //       let numberOfViews =
  //         parseInt(sessionStorage.getItem("numberOfViews"), 10) || 0;
  //       sessionStorage.setItem(
  //         "numberOfViews",
  //         JSON.stringify(numberOfViews + 1)
  //       );
  //       setPageViews(numberOfViews + 1);
  //     } catch (e) {
  //       setPageViews(1);
  //     }
  //   }
  // }, [isOpen]);

  const hideDialog = () => {
    setDialogStatus("dismissed");
    setHasOpen(true);
    setIsOpen(false);
  };

  return (
    <>
      <Transition appear show={isOpen && width < 768} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => {}}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div
              className="flex h-screen items-center justify-center text-center"
              id="download-drawer"
            >
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="translate-y-full"
                enterTo="translate-y-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-y-0"
                leaveTo="translate-y-full"
              >
                <Dialog.Panel className="fixed bottom-0 flex max-h-full w-full flex-col rounded-t-2xl bg-white p-6 text-center">
                  <Dialog.Title>
                    <div>
                      {pageViews == 1 ? (
                        <div className="mb-4">
                          <Image
                            src={NoPopUp}
                            alt="no-pop-up"
                            width={70}
                            height={70}
                          />
                          <p className="text-center font-poppins text-xl font-bold text-text-darkest">
                            <Trans>No pop-ups. No distractions.</Trans>
                          </p>
                          <p className="mt-2 text-center font-sen text-text-medium">
                            <Trans>Read with ease on Lumi News</Trans>
                          </p>
                        </div>
                      ) : pageViews <= 2 ? (
                        <div className="mb-4">
                          <Image
                            src={LastApp}
                            alt="your-last-news-app"
                            width={70}
                            height={70}
                          />
                          <p className="text-center font-poppins text-xl font-bold text-text-darkest">
                            <Trans>Your last news app, we promise.</Trans>
                          </p>
                          <p className="mt-2 text-center font-sen text-text-medium">
                            <Trans>100k+ Malaysians already use the app</Trans>
                          </p>
                        </div>
                      ) : pageViews <= 3 ? (
                        <div className="mb-4">
                          <Image
                            src={NumberOne}
                            alt="we-are-number-one"
                            width={70}
                            height={70}
                          />
                          <p className="text-center font-poppins text-xl font-bold text-text-darkest">
                            <Trans>We're #1 in Malaysia for a reason.</Trans>
                          </p>
                          <p className="mt-2 text-center font-sen text-text-medium">
                            <Trans>Read with ease on the app.</Trans>
                          </p>
                        </div>
                      ) : pageViews <= 4 ? (
                        <div className="mb-8">
                          <p className="text-center font-poppins text-[36px] font-bold text-text-darkest">
                            100,000
                          </p>
                          <p className="mb-4 text-center font-poppins text-text-medium">
                            <Trans>
                              Free news from 50+ sites. All in 1 app
                            </Trans>
                          </p>
                          <div className="flex justify-center">
                            <Image
                              src={HomeRating}
                              alt="rating"
                              height={20}
                              width={200}
                            />
                          </div>
                        </div>
                      ) : pageViews <= 5 ? (
                        <div className="mb-4">
                          <div className="relative">
                            <Image src={PublisherListPhoto} />
                            <div
                              className={`absolute inset-y-[-1px] left-[-1px] top-0 z-10 w-12 bg-gradient-to-r to-white transition-opacity duration-300`}
                              style={{
                                background:
                                  "linear-gradient(90deg, #FFFFFF 15%, rgba(255, 255, 255, 0) 100%)",
                              }}
                            ></div>
                            <div
                              className={`absolute inset-y-[-1px] right-[-1px] top-0 z-10 w-12 bg-gradient-to-r to-white transition-opacity duration-300`}
                              style={{
                                background:
                                  "linear-gradient(270deg, #FFFFFF 15%, rgba(255, 255, 255, 0) 100%)",
                              }}
                            ></div>
                          </div>

                          <p className="my-2 text-center font-poppins text-xl font-bold text-text-darkest">
                            <Trans>
                              News from 80+ sources you know and trust
                            </Trans>
                          </p>
                          <p className="text-center font-poppins text-text-medium">
                            <Trans>
                              100k+ Malaysians use Lumi News to stay informed
                            </Trans>
                          </p>
                        </div>
                      ) : (
                        <div className="mb-4">
                          <Image
                            src={LumiLogo}
                            alt="lumi-logo"
                            width={70}
                            height={70}
                          />
                          <p className="text-center font-poppins text-xl font-bold text-text-darkest">
                            <Trans>Start using Lumi news today</Trans>
                          </p>
                          <p className="mt-2 text-center font-sen text-text-medium">
                            <Trans>
                              The only app with Malaysia's top news sites
                            </Trans>
                          </p>
                        </div>
                      )}
                    </div>
                  </Dialog.Title>
                  <Link href={switchToApp}>
                    <a
                      className="mb-4 rounded-2xl bg-primary p-4 text-center font-sen text-white"
                      id="switch-to-app"
                    >
                      {pageViews <= 3 ? (
                        <Trans>Try it now for free</Trans>
                      ) : pageViews <= 5 ? (
                        <Trans>Get app for free</Trans>
                      ) : (
                        <Trans>Try it now. It's free</Trans>
                      )}
                    </a>
                  </Link>
                  <button
                    type="button"
                    className="text-center font-sen text-xs text-text-lightest"
                    onClick={hideDialog}
                    id="stay-on-web"
                  >
                    {pageViews <= 3 ? (
                      <Trans>Maybe later</Trans>
                    ) : pageViews <= 5 ? (
                      <Trans>Later</Trans>
                    ) : (
                      <Trans>I'll stay on web</Trans>
                    )}
                  </button>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default SwitchToAppDialog;
